import React from 'react';
import { CheckCircle } from 'lucide-react';
import Carousel from '@/ui/Carousel';
import imgNavbarStars from '@/assets/img/five-stars.svg';

export type Review = {
  id: string | number;
  rating: number;
  title?: string;
  description: string;
  author: string;
  isVerified: boolean;
  date?: string;
  subject?: string;
};

export type CustomerReviewsProps = {
  title?: string;
  reviews: Review[];
  variant?: 'list' | 'carousel';
  autoRotate?: boolean;
  perView?: number;
  minimal?: boolean;
  showDots?: boolean;
};

export const ReviewCard: React.FC<{ review: Review; minimal?: boolean; tabIndex?: number }> = ({
  review,
  minimal,
  tabIndex,
}) => {
  return (
    <div
      className={`bg-white border border-border-main rounded-2xl p-[14px] flex flex-col justify-start h-full gap-4 relative overflow-hidden shadow-sm`}
    >
      {!minimal && (
        <div className="text-primary font-bold text-body sm:text-subtitle">
          Trusted by thousands
        </div>
      )}

      <a
        href="https://www.trustpilot.com/review/fair.inc"
        target="_blank"
        rel="noopener noreferrer"
        className="flex justify-between items-center hover:opacity-80 transition-opacity cursor-pointer"
        tabIndex={tabIndex}
      >
        <img
          src={imgNavbarStars}
          alt="rating"
          width="120"
          height="22"
          className="w-[100px] h-auto sm:w-[120px] flex-shrink-0"
          style={{ aspectRatio: '120 / 22' }}
        />
        <span className="text-body font-semibold text-text-main">Trustpilot</span>
      </a>

      <p className={`text-text-main text-body font-normal leading-snug text-left`}>
        &ldquo;{review.description.replace(/[“”"]/g, '')}&rdquo;
      </p>

      <div className="text-body sm:text-body text-text-main mt-auto flex items-center gap-1.5 flex-wrap font-bold">
        {review.author}
        {review.isVerified && <CheckCircle size={14} className="text-primary shrink-0" />}
        {review.isVerified && <span className="text-primary font-medium text-body">Verified</span>}
      </div>

      {/* Decorative Quote Background */}
      <svg
        className="absolute bottom-[-12px] right-2 w-[55px] h-[55px] sm:w-[75px] sm:h-[75px] text-primary opacity-10 pointer-events-none z-0 select-none"
        viewBox="0 0 100 100"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x="50%"
          y="85%"
          dominantBaseline="middle"
          textAnchor="middle"
          fontFamily="Arial, Helvetica, sans-serif"
          fontWeight="900"
          fontSize="160"
        >
          ”
        </text>
      </svg>
    </div>
  );
};

export const CustomerReviews: React.FC<CustomerReviewsProps> = ({
  reviews,
  variant = 'list',
  minimal = false,
}) => {
  return (
    <div className="w-full">
      {variant === 'list' ? (
        <div className="flex flex-col gap-5">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} minimal={minimal} />
          ))}
        </div>
      ) : (
        <div className="relative group">
          <Carousel>
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} minimal={minimal} />
            ))}
          </Carousel>
        </div>
      )}
    </div>
  );
};

export default CustomerReviews;
